import React from 'react'
import type { Language } from '../survey/survey.types'
import { useBrandStore } from '../../stores/brandStore'
import '../styles/header.css'

interface RadcliffeHeaderProps {
  languages?: string[] | Language[]
  /**
   * Header display mode from survey configuration:
   * - 'banner': compact header
   * - 'full': large hero header
   * - null: no header
   */
  headerMode?: 'banner' | 'full' | null
}

export const RadcliffeHeader: React.FC<RadcliffeHeaderProps> = ({
  languages: _languages,
  headerMode = 'banner'
}) => {
  const config = useBrandStore((state) => state.config)
  const getImageUrl = useBrandStore((state) => state.getImageUrl)

  // Get brand logo and links
  const logoUrl = config?.images?.logo ? getImageUrl(config.images.logo) : ''
  const homeLink = config?.links?.privacyPolicy?.replace(/\/privacy.*/, '') || 'https://www.radcliffecardiology.com/'
  const brandName = config?.brandName || 'Radcliffe Cardiology'
  const brandTagline = config?.brandTagline || 'Lifelong learning for cardiovascular healthcare professionals'
  const fallbackLogoUrl = 'https://assets.radcliffecardiology.com/s3fs-public/2025-06/Radcliffe%20Cardiology%20%28light%29.svg'
  const logoAlt = config?.images?.logo ? `${brandName} logo` : 'Radcliffe Cardiology logo'

  // No header requested
  if (!headerMode) {
    return null
  }

  // Compact banner-style header
  if (headerMode === 'banner')
    return <>
      <div className='rc-header-small'>
        <div className="rc-header-container">
          <a
            href={homeLink}
            className="rc-logo"
            target="_blank"
            rel="noreferrer noopener"
            aria-label="Home"
          >
            <img
              src={logoUrl || fallbackLogoUrl}
              alt={logoAlt}
              loading="lazy"
            />
          </a>
        </div>
      </div>
    </>

  return (
    <>
      <header className="rc-site-header rc-hero" role="banner">
        <div className="rc-hero-inner">
          <div className="rc-header-top">
            <a
              href={homeLink}
              className="rc-logo"
              target="_blank"
              rel="noreferrer noopener"
              aria-label="Home"
            >
              <img
                src={logoUrl || fallbackLogoUrl}
                alt={logoAlt}
                loading="lazy"
              />
            </a>

            <div className="rc-header-actions" />
          </div>

          <div className="rc-hero-text">
            <h1 className="rc-title">{brandName}</h1>
            <p className="rc-subtitle">{brandTagline}</p>
          </div>
        </div>
      </header>
      <div className="rc-teal-strip">
        <div className="rc-teal-inner" />
      </div>
    </>
  )
}
